const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.partner.rhythmic.dev';
const CF_TEMPLATE_URL = import.meta.env.VITE_CF_TEMPLATE_URL || '';
const VENDOR_ACCOUNT_ID = import.meta.env.VITE_VENDOR_ACCOUNT_ID || '';

export interface TenantStatus {
  tenant_id: string;
  status: 'pending_setup' | 'pending_ace_setup' | 'pending_channel_setup' | 'active' | 'inactive' | 'suspended';
  workspace_id?: string;
  workspace_name?: string;
  customer_account_id?: string;
  ace_external_id: string;
  ace_role_configured: boolean;
  slack_configured: boolean;
  teams_configured?: boolean;
  teams_tenant_name?: string;
  hubspot_configured: boolean;
  salesforce_configured: boolean;
  crm_type?: 'hubspot' | 'salesforce' | null;  // Which CRM is configured
  crm_skipped?: boolean;  // True if CRM was explicitly skipped (ACE-only)
  allowed_channels: string[];
  features_enabled: string[];
  created_at?: string;
  updated_at?: string;
  session_token?: string;  // Session token for authenticated API calls
  // Organization details (collected in first setup step)
  organization_name?: string;
  admin_email?: string;
  timezone?: string;
  organization_configured?: boolean;
  // Marketplace entitlement
  entitlement_status?: 'active' | 'pending' | 'pending_confirmation' | 'check_failed';
}

export interface VerifyRoleResponse {
  status: 'success' | 'partial' | 'error';
  message: string;
  tenant_id: string;
  ace_access?: boolean;
  ace_error?: string;
}

export interface ApiError {
  error: string;
  details?: string;
}

export interface ScheduledReport {
  report_id?: string;
  name: string;
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number;    // 0-6 for weekly (0=Monday)
  day_of_month?: number;   // 1-28 for monthly
  time_utc: string;        // "HH:MM" in UTC
  timezone: string;        // IANA timezone for display
  channel_id: string;      // Target channel
  report_type: string;     // "pipeline_summary", "stale_opportunities", etc.
  custom_prompt?: string;  // Optional custom prompt
}

// Session token storage
const SESSION_TOKEN_KEY = 'partnerbuddy_session_token';

function getStoredToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string): void {
  try {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    // Ignore storage errors
  }
}

function clearToken(): void {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // Ignore storage errors
  }
}

// Get token from URL query param (for initial page load)
function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

// Generate request signature for sensitive operations
async function generateSignature(_tenantId: string, _body: string): Promise<{ signature: string; timestamp: string }> {
  // Note: In production, the signing secret should be fetched from a secure source
  // For now, we'll just send the timestamp and let the server validate with its secret
  // The server will allow unsigned requests in dev mode
  const timestamp = Math.floor(Date.now() / 1000).toString();
  return { signature: '', timestamp };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = getStoredToken() || getTokenFromUrl();
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requiresAuth: boolean = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add auth headers for authenticated requests
    if (requiresAuth) {
      Object.assign(headers, this.getAuthHeaders());
    }
    
    const response = await fetch(url, {
      ...options,
      headers,
      // Note: Don't use credentials: 'include' - we use Authorization header tokens, not cookies
      // and 'include' is incompatible with Access-Control-Allow-Origin: '*' from OPTIONS preflight
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      // Clear token on auth errors
      if (response.status === 401) {
        clearToken();
      }
      throw new Error(error.details || error.error || 'Request failed');
    }

    return data as T;
  }

  private async signedRequest<T>(
    endpoint: string,
    tenantId: string,
    options: RequestInit = {}
  ): Promise<T> {
    const body = options.body as string || '';
    const { signature, timestamp } = await generateSignature(tenantId, body);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Timestamp': timestamp,
      ...(options.headers as Record<string, string> || {}),
      ...this.getAuthHeaders(),
    };

    // Only add signature if we have one
    if (signature) {
      headers['X-Signature'] = signature;
    }
    
    return this.request<T>(endpoint, { ...options, headers }, true);
  }

  async getTenantStatus(tenantId: string): Promise<TenantStatus> {
    // For initial load, pass token as query param if we have it from URL
    const urlToken = getTokenFromUrl();
    const endpoint = urlToken 
      ? `/register/tenant/${tenantId}?token=${encodeURIComponent(urlToken)}`
      : `/register/tenant/${tenantId}`;
    
    const result = await this.request<TenantStatus>(endpoint, {}, true);
    
    // Store the session token from the response
    if (result.session_token) {
      storeToken(result.session_token);
    }
    
    return result;
  }

  async verifyAceRole(tenantId: string, roleArn: string): Promise<VerifyRoleResponse> {
    return this.request<VerifyRoleResponse>('/register/ace/verify', {
      method: 'POST',
      body: JSON.stringify({
        tenant_id: tenantId,
        role_arn: roleArn,
      }),
    });
  }

  async updateOrganization(
    tenantId: string,
    data: {
      organization_name: string;
      admin_email: string;
      timezone?: string;
    }
  ): Promise<void> {
    await this.request(`/register/tenant/${tenantId}/organization`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateAllowedChannels(tenantId: string, channels: string[], activate: boolean = true): Promise<void> {
    await this.signedRequest(`/register/tenant/${tenantId}/channels`, tenantId, {
      method: 'PUT',
      body: JSON.stringify({ channels, activate }),
    });
  }

  async saveHubSpotCredentials(tenantId: string, apiKey: string, portalId?: string): Promise<void> {
    await this.signedRequest(`/register/tenant/${tenantId}/hubspot`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ 
        api_key: apiKey,
        portal_id: portalId 
      }),
    });
  }

  async skipHubSpot(tenantId: string): Promise<void> {
    await this.request(`/register/tenant/${tenantId}/hubspot/skip`, {
      method: 'POST',
    });
  }

  // HubSpot OAuth methods (new)
  async saveHubSpotOAuthCredentials(
    tenantId: string,
    clientId: string,
    clientSecret: string,
    portalId?: string
  ): Promise<void> {
    await this.signedRequest(`/register/tenant/${tenantId}/hubspot/credentials`, tenantId, {
      method: 'POST',
      body: JSON.stringify({
        client_id: clientId,
        client_secret: clientSecret,
        portal_id: portalId
      }),
    });
  }

  getHubSpotOAuthUrl(tenantId: string): string {
    // Include session token in OAuth flow
    const token = getStoredToken();
    const params = new URLSearchParams();
    if (token) {
      params.set('token', token);
    }
    return `${this.baseUrl}/register/tenant/${tenantId}/hubspot/start?${params.toString()}`;
  }

  // Salesforce CRM methods
  async saveSalesforceCredentials(
    tenantId: string,
    instanceUrl: string,
    clientId: string,
    clientSecret: string
  ): Promise<void> {
    await this.signedRequest(`/register/tenant/${tenantId}/salesforce/credentials`, tenantId, {
      method: 'POST',
      body: JSON.stringify({
        instance_url: instanceUrl,
        client_id: clientId,
        client_secret: clientSecret
      }),
    });
  }

  getSalesforceOAuthUrl(tenantId: string): string {
    // Include session token in OAuth flow
    const token = getStoredToken();
    const params = new URLSearchParams();
    if (token) {
      params.set('token', token);
    }
    return `${this.baseUrl}/register/tenant/${tenantId}/salesforce/start?${params.toString()}`;
  }

  async skipCRM(tenantId: string): Promise<void> {
    await this.request(`/register/tenant/${tenantId}/crm/skip`, {
      method: 'POST',
    });
  }

  // Report configuration methods (legacy single-report)
  async updateReportConfig(
    tenantId: string,
    config: {
      enabled: boolean;
      frequency: 'daily' | 'weekly' | 'monthly';
      day_of_week?: number;
      day_of_month?: number;
      time_utc: string;
      timezone: string;
      channel_id: string;
      report_type: string;
      custom_prompt?: string;
    }
  ): Promise<void> {
    await this.signedRequest(`/register/tenant/${tenantId}/report-config`, tenantId, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Scheduled Reports CRUD (multiple reports)
  async listScheduledReports(tenantId: string): Promise<ScheduledReport[]> {
    const result = await this.request<{ reports: ScheduledReport[] }>(
      `/register/tenant/${tenantId}/reports`
    );
    return result.reports;
  }

  async createScheduledReport(
    tenantId: string,
    report: Omit<ScheduledReport, 'report_id'>
  ): Promise<string> {
    const result = await this.signedRequest<{ report_id: string }>(
      `/register/tenant/${tenantId}/reports`,
      tenantId,
      {
        method: 'POST',
        body: JSON.stringify(report),
      }
    );
    return result.report_id;
  }

  async updateScheduledReport(
    tenantId: string,
    reportId: string,
    report: Omit<ScheduledReport, 'report_id'>
  ): Promise<void> {
    await this.signedRequest(`/register/tenant/${tenantId}/reports/${reportId}`, tenantId, {
      method: 'PUT',
      body: JSON.stringify(report),
    });
  }

  async deleteScheduledReport(tenantId: string, reportId: string): Promise<void> {
    await this.request(`/register/tenant/${tenantId}/reports/${reportId}`, {
      method: 'DELETE',
    });
  }

  getSlackOAuthUrl(tenantId: string): string {
    // Include session token in OAuth flow
    const token = getStoredToken();
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (token) {
      params.set('token', token);
    }
    return `${this.baseUrl}/register/slack/start?${params.toString()}`;
  }

  async confirmTeamsManual(tenantId: string, teamsTenantId: string): Promise<{ success: boolean; next_step: string }> {
    return this.request(`/register/tenant/${tenantId}/teams/manual-confirm`, {
      method: 'POST',
      body: JSON.stringify({ teams_tenant_id: teamsTenantId }),
    });
  }

  getCloudFormationUrl(externalId: string, vendorAccountId?: string): string {
    const templateUrl = CF_TEMPLATE_URL;
    const accountId = vendorAccountId || VENDOR_ACCOUNT_ID;
    
    if (!templateUrl || !accountId) {
      console.error('CloudFormation template URL or vendor account ID not configured');
      return '#';
    }
    
    const params = new URLSearchParams({
      templateURL: templateUrl,
      stackName: 'partnerbuddy-cross-account',
      'param_ExternalId': externalId,
      'param_PartnerBuddyAccountId': accountId,
    });
    return `https://console.aws.amazon.com/cloudformation/home?#/stacks/create/review?${params.toString()}`;
  }
}

export const api = new ApiClient(API_BASE_URL);
